import { useEffect, useRef, useState } from "react";
import { downloadFile } from "../api/client";
import { Icon } from "./icons";
import { toast } from "./toast";

interface DawFormat {
  name: string;
  desc: string;
  fmt: string | null; // null = documented format not active yet ("soon")
  /** Its marker positions are SAMPLE COUNTS, so the file has to be told which
   *  clock to count them on. Only Pro Tools; every other format here writes a
   *  clock time and is rate-agnostic. */
  rated?: boolean;
}

/** The two rates an audiobook master actually arrives at. */
const RATES = [
  { v: 44100, label: "44.1" },
  { v: 48000, label: "48" },
] as const;

// Order per the design — Pro Tools first as the most common audiobook-post tool.
// `fmt` is the backend export id; null rows are surfaced as "soon".
const DAWS: DawFormat[] = [
  { name: "Pro Tools", desc: "Session (.ptx) markers", fmt: "protools", rated: true },
  { name: "Audition", desc: "Marker list", fmt: "audition" },
  { name: "Reaper", desc: "Region & marker CSV", fmt: "reaper" },
  { name: "Audacity", desc: "Label track", fmt: "audacity" },
  { name: "Studio One", desc: "MIDI markers", fmt: "studio-one" },
  { name: "Twisted Wave", desc: "Marker list", fmt: null },
  { name: "Hindenburg", desc: "Marker list", fmt: null },
  { name: "Nuendo", desc: "Marker track", fmt: null },
  { name: "Cubase", desc: "Marker track", fmt: null },
  { name: "Sound Forge", desc: "Regions list", fmt: null },
  { name: "iZotope RX", desc: "Marker list", fmt: null },
  { name: "GoldWave", desc: "Cue points", fmt: "goldwave" },
  { name: "BWF (Broadcast Wave)", desc: "Embedded cues", fmt: null },
];

/**
 * Sharing Centre — the share/export control in the Pickups Package header. A
 * calm action bar (Copy share link + Export) that opens a grouped, searchable
 * popover: two spreadsheet formats + the DAW-marker list. `exportBase` is the
 * `…/export` endpoint root (owner or public token), `copyUrl` returns the link
 * to copy (owner mints/enables it; the public view copies its own URL).
 */
export function SharingCentre({
  exportBase,
  copyUrl,
  title,
  pickupCount,
  basis = "chapter",
  shareEnabled,
  onRevoke,
  building,
  onFrameUpload,
}: {
  exportBase: string;
  copyUrl: () => Promise<string | null>;
  title: string;
  pickupCount: number;
  basis?: "chapter" | "book";
  shareEnabled?: boolean;
  onRevoke?: () => void;
  // Package still assembling: the pickup list is loading, or flags picked in this
  // browser haven't reached the server yet. Both SHARE and EXPORT are gated on
  // it, because both are built server-side from the Pickup rows — a link minted
  // now would be missing those pickups, and so would a spreadsheet. Deliberately
  // NOT gated on the page's text-filling pass: that only enriches what's drawn on
  // screen, so blocking an export for it would be blocking a correct file.
  building?: boolean;
  // Owner only: open "Upload to Frame" (the pickups as comments on the review
  // files in Frame.io). Absent on the public share page, which has no Essentials.
  onFrameUpload?: () => void;
}) {
  const [open, setOpen] = useState(false);
  // Which clock the Pro Tools session is built on. 44.1 kHz is the audiobook
  // master standard and stays the default; 48 kHz is what the work arrives on
  // when the book started life alongside video.
  const [rate, setRate] = useState<number>(44100);
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);
  const clusterRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const focusT = setTimeout(() => searchRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (clusterRef.current && !clusterRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      clearTimeout(focusT);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  async function copy() {
    const url = await copyUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked */
    }
  }

  function doExport(fmt: string, sampleRate?: number) {
    // Surface failures — a swallowed error looks like a dead button (e.g. a
    // server-side export error would just… not download, with no explanation).
    const rate = sampleRate ? `&sample_rate=${sampleRate}` : "";
    downloadFile(`${exportBase}/${fmt}?basis=${basis}${rate}`).catch((e: any) =>
      toast(e?.message || "That export didn't download — please try again.", "error")
    );
    setOpen(false);
  }

  const q = query.trim().toLowerCase();
  const filtered = q ? DAWS.filter((d) => d.name.toLowerCase().includes(q)) : DAWS;

  return (
    <div className="share-cluster" ref={clusterRef}>
      <div className="share-bar">
        <button
          className={`btn-share btn-share-primary${building ? " building" : ""}`}
          onClick={copy}
          disabled={building}
          title={building ? "The package is still assembling — one moment" : undefined}
        >
          {building ? (
            <><span className="spinner" style={{ width: 15, height: 15 }} /> Building the package…</>
          ) : (
            <><Icon name={copied ? "check" : "share"} size={16} /> {copied ? "Link copied" : "Copy share link"}</>
          )}
        </button>
        <button
          className={`btn-share btn-share-secondary${open ? " open" : ""}`}
          aria-haspopup="true"
          aria-expanded={open && !building}
          disabled={building}
          title={building ? "The package is still assembling — an export now could miss pickups" : undefined}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        >
          <Icon name="download" size={16} />
          Export
          <Icon name="chevron-down" size={15} className="chev" />
        </button>
      </div>

      <div className="share-note">
        Anyone with the link can view — no login.
        {shareEnabled && onRevoke && (
          <> · <button className="share-stop" onClick={onRevoke}>Stop sharing</button></>
        )}
      </div>

      {open && !building && (
        <div className="ep" role="dialog" aria-label="Export pickups">
          <div className="ep-head">
            <div>
              <div className="ep-title">Export pickups</div>
              <div className="ep-sub">
                {pickupCount} pickup{pickupCount === 1 ? "" : "s"} · {title}
              </div>
            </div>
            <button className="ep-close" aria-label="Close" onClick={() => setOpen(false)}>
              <Icon name="x" size={18} />
            </button>
          </div>

          {onFrameUpload && (
            <div className="ep-group">
              <div className="ep-group-row"><span className="ep-group-label">Frame.io</span></div>
              <button className="ep-daw" onClick={() => { setOpen(false); onFrameUpload(); }}>
                <span className="ep-daw-name">Upload to Frame</span>
                <span className="ep-daw-desc">Comments on the review files</span>
                <Icon name="upload" size={16} className="ep-daw-dl" />
              </button>
            </div>
          )}

          <div className={`ep-group${onFrameUpload ? " ep-group-top" : ""}`}>
            <div className="ep-group-row"><span className="ep-group-label">Spreadsheet</span></div>
            <div className="ep-files">
              <button className="ep-file" onClick={() => doExport("xlsx")}>
                <span className="fic xls"><Icon name="file-spreadsheet" size={16} /></span>
                <span className="ft"><b>Excel</b><span>.xlsx</span></span>
              </button>
              <button className="ep-file" onClick={() => doExport("csv")}>
                <span className="fic csv"><Icon name="file-text" size={16} /></span>
                <span className="ft"><b>CSV</b><span>.csv</span></span>
              </button>
            </div>
          </div>

          <div className="ep-group ep-group-top">
            <div className="ep-group-row">
              <span className="ep-group-label">DAW markers</span>
              <span className="ep-count">{q ? `${filtered.length} of ${DAWS.length}` : `${DAWS.length} formats`}</span>
            </div>
            <div className="ep-search">
              <Icon name="search" size={16} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find your DAW…"
                autoComplete="off"
              />
            </div>
            {filtered.length === 0 ? (
              <div className="ep-empty">No DAW matches that name.</div>
            ) : (
              <div className="ep-list">
                {filtered.map((d) =>
                  d.fmt && d.rated ? (
                    // Its own row shape, because the rate control is a real
                    // button and cannot live inside the row's button.
                    <div key={d.name} className="ep-daw ep-daw-rated">
                      <button className="ep-daw-main" onClick={() => doExport(d.fmt!, rate)}>
                        <span className="ep-daw-name">{d.name}</span>
                        <span className="ep-daw-desc">{d.desc}</span>
                        <Icon name="download" size={16} className="ep-daw-dl" />
                      </button>
                      <span className="ep-rate" role="group" aria-label="Session sample rate">
                        {RATES.map((r) => (
                          <button key={r.v} type="button"
                            className={rate === r.v ? "on" : ""}
                            aria-pressed={rate === r.v}
                            title={`Build the session at ${r.label} kHz — match your own session, or the markers land in the wrong place`}
                            onClick={() => setRate(r.v)}>
                            {r.label}
                          </button>
                        ))}
                        <span className="ep-rate-unit">kHz</span>
                      </span>
                    </div>
                  ) : d.fmt ? (
                    <button key={d.name} className="ep-daw" onClick={() => doExport(d.fmt!)}>
                      <span className="ep-daw-name">{d.name}</span>
                      <span className="ep-daw-desc">{d.desc}</span>
                      <Icon name="download" size={16} className="ep-daw-dl" />
                    </button>
                  ) : (
                    <div key={d.name} className="ep-daw ep-daw-soon" aria-disabled="true" title="Coming soon">
                      <span className="ep-daw-name">{d.name}</span>
                      <span className="ep-daw-desc">{d.desc}</span>
                      <span className="ep-soon">Soon</span>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          <div className="ep-foot">
            <Icon name="map-pin" size={14} /> Markers land at each pickup’s exact timecode.
          </div>
        </div>
      )}
    </div>
  );
}
